import { TranscriptionWord } from '../types';
import { getMultipleWordPhonemes, WordPhonemes, PhonemeBreakdown as ApiPhonemeBreakdown } from '../services/api';

// Type definitions for phoneme analysis
interface PhonemeInfo {
  ipa: string;
  sound?: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

interface PhonemeBreakdown {
  phoneme: string;
  ipa: string;
  difficulty: 'easy' | 'medium' | 'hard';
  tip: string;
}

interface CommonMistake {
  issue: string;
  mistakes: string[];
  tip: string;
}

interface PronunciationTip {
  type: 'error' | 'warning' | 'info' | 'tip';
  message: string;
}

interface Pause {
  afterWord: string;
  duration: number;
  type: 'long' | 'medium' | 'short';
}

interface WordTiming {
  word: string;
  duration: number;
  durationMs: string;
  speed: 'fast' | 'slow' | 'normal';
}

interface TimingAnalysis {
  totalDuration: number;
  totalDurationSec: string;
  wordsPerMinute: number;
  nativeWPM: number;
  paceComparison: string;
  avgWordDuration: number;
  pauses: Pause[];
  wordTimings: WordTiming[];
  fluencyScore: number;
}

// Common English phoneme patterns and IPA representations
const PHONEME_MAP: Record<string, PhonemeInfo> = {
  // Vowels
  'a': { ipa: '/æ/', sound: 'as in "cat"', difficulty: 'medium' },
  'e': { ipa: '/ɛ/', sound: 'as in "bed"', difficulty: 'easy' },
  'i': { ipa: '/ɪ/', sound: 'as in "bit"', difficulty: 'easy' },
  'o': { ipa: '/ɒ/', sound: 'as in "hot"', difficulty: 'medium' },
  'u': { ipa: '/ʌ/', sound: 'as in "cup"', difficulty: 'medium' },
  'ee': { ipa: '/iː/', sound: 'as in "see"', difficulty: 'easy' },
  'oo': { ipa: '/uː/', sound: 'as in "moon"', difficulty: 'easy' },
  'ou': { ipa: '/aʊ/', sound: 'as in "out"', difficulty: 'hard' },
  'ow': { ipa: '/aʊ/', sound: 'as in "cow"', difficulty: 'hard' },
  'oi': { ipa: '/ɔɪ/', sound: 'as in "boy"', difficulty: 'medium' },
  'ay': { ipa: '/eɪ/', sound: 'as in "say"', difficulty: 'medium' },
  'ai': { ipa: '/eɪ/', sound: 'as in "rain"', difficulty: 'medium' },

  // Consonants
  'th': { ipa: '/θ/', sound: 'voiceless "th"', difficulty: 'hard' },
  'th_voiced': { ipa: '/ð/', sound: 'voiced "th"', difficulty: 'hard' },
  'sh': { ipa: '/ʃ/', sound: 'as in "ship"', difficulty: 'medium' },
  'ch': { ipa: '/tʃ/', sound: 'as in "chip"', difficulty: 'medium' },
  'ng': { ipa: '/ŋ/', sound: 'as in "sing"', difficulty: 'medium' },
  'zh': { ipa: '/ʒ/', sound: 'as in "vision"', difficulty: 'hard' },
  'r': { ipa: '/ɹ/', sound: 'American "r"', difficulty: 'hard' },
  'l': { ipa: '/l/', sound: 'as in "light"', difficulty: 'medium' },
  'w': { ipa: '/w/', sound: 'as in "water"', difficulty: 'easy' },
  'y': { ipa: '/j/', sound: 'as in "yes"', difficulty: 'easy' },
};

// Word-specific phoneme breakdowns
const WORD_PHONEMES: Record<string, PhonemeBreakdown[]> = {
  'the': [
    { phoneme: 'th', ipa: '/ð/', difficulty: 'hard', tip: 'Place tongue between teeth, vibrate vocal cords' },
    { phoneme: 'ə', ipa: '/ə/', difficulty: 'easy', tip: 'Relaxed, neutral vowel sound' }
  ],
  'quick': [
    { phoneme: 'kw', ipa: '/kw/', difficulty: 'medium', tip: 'Start with back of tongue, add "w" lip rounding' },
    { phoneme: 'i', ipa: '/ɪ/', difficulty: 'easy', tip: 'Short "i" sound, tongue high and forward' },
    { phoneme: 'k', ipa: '/k/', difficulty: 'easy', tip: 'Back of tongue touches soft palate' }
  ],
  'brown': [
    { phoneme: 'br', ipa: '/br/', difficulty: 'medium', tip: 'Lips together for "b", then quick "r"' },
    { phoneme: 'ow', ipa: '/aʊ/', difficulty: 'hard', tip: 'Diphthong: start with "ah", glide to "oo"' },
    { phoneme: 'n', ipa: '/n/', difficulty: 'easy', tip: 'Tongue tip touches roof of mouth' }
  ],
  'fox': [
    { phoneme: 'f', ipa: '/f/', difficulty: 'easy', tip: 'Upper teeth touch lower lip, blow air' },
    { phoneme: 'o', ipa: '/ɒ/', difficulty: 'medium', tip: 'Open mouth, rounded lips' },
    { phoneme: 'ks', ipa: '/ks/', difficulty: 'medium', tip: 'Quick "k" followed by "s"' }
  ],
  'jumps': [
    { phoneme: 'j', ipa: '/dʒ/', difficulty: 'medium', tip: 'Tongue touches roof, release with voice' },
    { phoneme: 'u', ipa: '/ʌ/', difficulty: 'medium', tip: 'Short "u", mouth slightly open' },
    { phoneme: 'mps', ipa: '/mps/', difficulty: 'hard', tip: 'Close lips for "m", then "p", then "s"' }
  ],
  'over': [
    { phoneme: 'o', ipa: '/oʊ/', difficulty: 'medium', tip: 'Diphthong: start with "oh", glide to "oo"' },
    { phoneme: 'v', ipa: '/v/', difficulty: 'medium', tip: 'Upper teeth on lower lip, vibrate' },
    { phoneme: 'er', ipa: '/ər/', difficulty: 'hard', tip: 'R-colored vowel, tongue curled back' }
  ],
  'lazy': [
    { phoneme: 'l', ipa: '/l/', difficulty: 'medium', tip: 'Tongue tip touches ridge behind teeth' },
    { phoneme: 'ay', ipa: '/eɪ/', difficulty: 'medium', tip: 'Diphthong: "eh" gliding to "ee"' },
    { phoneme: 'z', ipa: '/z/', difficulty: 'easy', tip: 'Like "s" but with voice' },
    { phoneme: 'ee', ipa: '/i/', difficulty: 'easy', tip: 'Long "e" sound, smile shape' }
  ],
  'dog': [
    { phoneme: 'd', ipa: '/d/', difficulty: 'easy', tip: 'Tongue tip touches ridge, release with voice' },
    { phoneme: 'o', ipa: '/ɔː/', difficulty: 'medium', tip: 'Open, rounded mouth' },
    { phoneme: 'g', ipa: '/ɡ/', difficulty: 'easy', tip: 'Back of tongue touches soft palate' }
  ]
};

// Common pronunciation mistakes by native language
const COMMON_MISTAKES: Record<string, CommonMistake> = {
  'th': {
    issue: 'TH sound',
    mistakes: ['Often pronounced as "d", "t", "s", or "z"'],
    tip: 'Place tongue tip between teeth and blow air gently'
  },
  'r': {
    issue: 'American R',
    mistakes: ['Trilled or tapped like Spanish/Italian R', 'Too soft like British R'],
    tip: 'Curl tongue back without touching the roof of mouth'
  },
  'v_w': {
    issue: 'V vs W distinction',
    mistakes: ['Confusing "v" and "w" sounds'],
    tip: 'V: teeth on lip with vibration. W: rounded lips, no teeth contact'
  },
  'short_long_vowels': {
    issue: 'Vowel length',
    mistakes: ['Making short vowels too long', 'Not distinguishing "ship" vs "sheep"'],
    tip: 'Pay attention to vowel duration - it changes word meaning'
  }
};

// Generate basic phoneme analysis for words not in dictionary
const generateBasicPhonemes = (word: string): PhonemeBreakdown[] => {
  const phonemes: PhonemeBreakdown[] = [];
  let i = 0;

  while (i < word.length) {
    // Check for digraphs first
    const twoChar = word.slice(i, i + 2);
    if (['th', 'sh', 'ch', 'ng', 'ee', 'oo', 'ou', 'ow', 'oi', 'ay', 'ai'].includes(twoChar)) {
      const info = PHONEME_MAP[twoChar] || { ipa: `/${twoChar}/`, difficulty: 'medium' as const };
      phonemes.push({
        phoneme: twoChar,
        ipa: info.ipa,
        difficulty: info.difficulty,
        tip: info.sound || `Pronounce "${twoChar}" clearly`
      });
      i += 2;
    } else {
      const char = word[i];
      const info = PHONEME_MAP[char] || { ipa: `/${char}/`, difficulty: 'easy' as const };
      phonemes.push({
        phoneme: char,
        ipa: info.ipa,
        difficulty: info.difficulty,
        tip: info.sound || `Standard "${char}" sound`
      });
      i += 1;
    }
  }

  return phonemes;
};

// Get phoneme breakdown for a word
export const getPhonemeBreakdown = (word: string): PhonemeBreakdown[] => {
  const normalized = word.toLowerCase().replace(/[^a-z]/g, '');

  if (WORD_PHONEMES[normalized]) {
    return WORD_PHONEMES[normalized];
  }

  // Generate basic phoneme breakdown for unknown words
  return generateBasicPhonemes(normalized);
};

// Get pronunciation tips based on confidence score
export const getPronunciationTips = (word: string, confidence: number, wasCorrect: boolean): PronunciationTip[] => {
  const tips: PronunciationTip[] = [];
  const normalized = word.toLowerCase().replace(/[^a-z]/g, '');

  if (!wasCorrect) {
    tips.push({
      type: 'error',
      message: 'This word was not recognized correctly. Try speaking more clearly.'
    });
  }

  if (confidence < 0.6) {
    tips.push({
      type: 'warning',
      message: 'Low confidence - ensure clear articulation of each syllable.'
    });
  } else if (confidence < 0.8) {
    tips.push({
      type: 'info',
      message: 'Good attempt! Focus on the difficult sounds for improvement.'
    });
  }

  // Check for commonly mispronounced patterns
  if (normalized.includes('th')) {
    tips.push({
      type: 'tip',
      message: COMMON_MISTAKES['th'].tip
    });
  }

  if (normalized.includes('r') && confidence < 0.85) {
    tips.push({
      type: 'tip',
      message: COMMON_MISTAKES['r'].tip
    });
  }

  return tips;
};

// Calculate fluency score based on pace and pauses
const calculateFluencyScore = (wpm: number, pauses: Pause[], wordCount: number): number => {
  let score = 100;

  // Penalize for being too slow or too fast
  const NATIVE_WPM = 150;
  const wpmDiff = Math.abs(wpm - NATIVE_WPM) / NATIVE_WPM;
  score -= wpmDiff * 30;

  // Penalize for excessive pauses
  const longPauses = pauses.filter(p => p.type === 'long').length;
  const mediumPauses = pauses.filter(p => p.type === 'medium').length;
  score -= longPauses * 10;
  score -= mediumPauses * 3;

  return Math.max(0, Math.min(100, Math.round(score)));
};

// Calculate timing analysis
export const analyzeTimings = (words: TranscriptionWord[], _targetText: string): TimingAnalysis | null => {
  if (!words || words.length === 0) {
    return null;
  }
  const totalDuration = words[words.length - 1].end - words[0].start;
  const avgWordDuration = totalDuration / words.length;

  // Reference speaking rates (words per minute)
  const NATIVE_WPM = 150; // Average native speaker
  const actualWPM = (words.length / totalDuration) * 60000;

  // Calculate pauses between words
  const pauses: Pause[] = [];
  for (let i = 1; i < words.length; i++) {
    const gap = words[i].start - words[i - 1].end;
    if (gap > 100) { // Pause > 100ms
      pauses.push({
        afterWord: words[i - 1].text,
        duration: gap,
        type: gap > 500 ? 'long' : gap > 200 ? 'medium' : 'short'
      });
    }
  }

  // Analyze individual word durations
  const wordTimings: WordTiming[] = words.map((word, index) => {
    const duration = word.end - word.start;
    const expectedDuration = 300; // Average expected duration in ms

    return {
      word: word.text,
      duration,
      durationMs: `${duration}ms`,
      speed: duration < expectedDuration * 0.7 ? 'fast' :
             duration > expectedDuration * 1.5 ? 'slow' : 'normal'
    };
  });

  return {
    totalDuration,
    totalDurationSec: (totalDuration / 1000).toFixed(2),
    wordsPerMinute: Math.round(actualWPM),
    nativeWPM: NATIVE_WPM,
    paceComparison: actualWPM < NATIVE_WPM * 0.7 ? 'slower than native' :
                    actualWPM > NATIVE_WPM * 1.3 ? 'faster than native' : 'similar to native',
    avgWordDuration: Math.round(avgWordDuration),
    pauses,
    wordTimings,
    fluencyScore: calculateFluencyScore(actualWPM, pauses, words.length)
  };
};

// Get difficulty assessment for a word
export const getWordDifficulty = (word: string): 'easy' | 'medium' | 'hard' => {
  const phonemes = getPhonemeBreakdown(word);
  const hardPhonemes = phonemes.filter(p => p.difficulty === 'hard').length;
  const mediumPhonemes = phonemes.filter(p => p.difficulty === 'medium').length;

  if (hardPhonemes >= 2) return 'hard';
  if (hardPhonemes >= 1 || mediumPhonemes >= 2) return 'medium';
  return 'easy';
};

// Async function to fetch phonemes from API with fallback to local analysis
export const fetchWordPhonemes = async (words: string[]): Promise<Map<string, PhonemeBreakdown[]>> => {
  const phonemeMap = new Map<string, PhonemeBreakdown[]>();

  try {
    // Normalize words and remove duplicates
    const uniqueWords = Array.from(new Set(words.map(w => w.toLowerCase().replace(/[^a-z']/g, ''))));

    // Fetch from API
    const apiResults = await getMultipleWordPhonemes(uniqueWords);

    // Convert API results to local format
    for (const result of apiResults) {
      const phonemes: PhonemeBreakdown[] = result.phonemes.map(p => ({
        phoneme: p.phoneme,
        ipa: p.ipa.startsWith('/') ? p.ipa : `/${p.ipa}/`,
        difficulty: p.difficulty,
        tip: p.tip,
      }));
      phonemeMap.set(result.word, phonemes);
    }

    return phonemeMap;
  } catch (error) {
    console.error('Failed to fetch phonemes from API, using local fallback:', error);

    // Use local fallback for all words
    for (const word of words) {
      const normalized = word.toLowerCase().replace(/[^a-z']/g, '');
      if (!phonemeMap.has(normalized)) {
        phonemeMap.set(normalized, getPhonemeBreakdown(normalized));
      }
    }

    return phonemeMap;
  }
};

// Re-export API types for convenience
export type { WordPhonemes, ApiPhonemeBreakdown };

export { COMMON_MISTAKES, WORD_PHONEMES };
